/**
 * Disclosure Agent: state seller-disclosure forms (Wisconsin RECR).
 *
 * The question set is served by the backend from a versioned item file rather
 * than hardcoded here — the form is statutory (Wis. Stat. § 709.03) and its
 * items change on dates certain, so the client must never carry its own copy.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import APIAgent from './apiAgent';

const base = (houseId) => `/api/houses/${houseId}/disclosure`;

/** `?a=1&b=2` from the set (non-empty) values of `params`, or ''. */
const toQuery = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ).toString();
  return query ? `?${query}` : '';
};

/** The caption fields a preview is rendered from — and all it is sent. */
const PREVIEW_PARAMS = ['county', 'municipality_type', 'municipality_name'];

export const DisclosureAPI = {
  /** Fetch the question set in force plus any saved answers. */
  getForm: (houseId, signedOn) =>
    APIAgent.request(
      `${base(houseId)}/form${signedOn ? `?signed_on=${signedOn}` : ''}`,
      'GET'
    ),

  /** Status for the wizard step and the publish gate. */
  getStatus: (houseId) => APIAgent.request(base(houseId), 'GET'),

  /**
   * Merge a partial set of answers into the draft.
   *
   * `edition` is the edition of the questions the answers were given to
   * (`form.edition`). When it is missing or no longer current the server
   * refuses with a 409 rather than file answers under reworded questions.
   *
   * `form` is the form the page showed (`form.form`: "recr" | "vldr"). The
   * server picks the form from the listing's paperwork; when the property
   * type changed since the page loaded it refuses with a 409 instead of
   * filing RECR answers on a VLDR (or the reverse).
   */
  saveAnswers: (houseId, answers, edition, form) =>
    APIAgent.request(`${base(houseId)}/answers`, 'PUT', {
      answers,
      edition,
      ...(form ? { form } : {}),
    }),

  /**
   * The report exactly as it will print, unsigned, for the owner to review.
   *
   * Rendered server-side from the SAVED answers — save pending answers first.
   * Resolves to `{ html, sha256, signature_date }`; signing quotes the
   * `sha256`, so the owner signs precisely what they reviewed. Invalid
   * caption values are a 422 with `detail.problems`.
   *
   * Only the caption is sent. The signing capacity and any owner's name are
   * never put in the URL — URLs end up in logs — and the server doesn't use
   * them for the preview anyway: they only affect the signed render.
   *
   * @param {number} houseId
   * @param {object} caption - `county`, `municipality_type` and
   *   `municipality_name`; anything else is dropped
   */
  getPreview: (houseId, caption = {}) =>
    APIAgent.request(
      `${base(houseId)}/preview${toQuery(
        Object.fromEntries(PREVIEW_PARAMS.map((key) => [key, caption[key]]))
      )}`,
      'GET',
      null,
      {},
      { timeout: 30000 }
    ),

  /**
   * Start (or resume) the owner's DocuSign signature on the report.
   *
   * Replaces the typed-name `/complete` (now 410 Gone). The payload is the
   * certification without any typed name: `{ certify: true, county,
   * municipality_type, municipality_name, capacity, represented_owner?,
   * representative_capacity?, attestation_version, preview_sha256 }`.
   * Resolves to `{ document_id, envelope_id, signing_url }`; the caller sends
   * the browser to `signing_url`, and DocuSign returns the owner to
   * `/seller/listings/{houseId}/disclosure-return`.
   *
   * While an envelope is out the report's status is `signing` and its answers
   * are locked; calling this again reuses that envelope and returns a fresh
   * signing URL ("Continue signing in DocuSign").
   *
   * Errors: a 422 whose `detail.problems` entries are prefixed `CERT:`,
   * `CAPTION:` or `CAPACITY:`, alongside any per-item problems; a 409 when the
   * preview or the attestation is out of date or reports aren't accepted yet;
   * a 503 when DocuSign is unavailable. The server renders the PDF and creates
   * the envelope, which routinely outruns the 10s default on a cold Cloud Run
   * instance, so the call gets a long timeout.
   */
  sign: (houseId, payload) =>
    APIAgent.request(
      `${base(houseId)}/sign`,
      'POST',
      payload || {},
      {},
      { timeout: 60000 }
    ),

  /**
   * Have the server read the envelope's live status from DocuSign and finish
   * the report if it was signed. Idempotent, so it is safe to poll. Resolves
   * to the same payload as `getStatus` (`status`: draft | signing | completed).
   */
  reconcile: (houseId) =>
    APIAgent.request(
      `${base(houseId)}/reconcile`,
      'POST',
      {},
      {},
      { timeout: 30000 }
    ),

  /**
   * The seller's signed condition report, for a signed-in buyer to read
   * before making an offer. Sign-in required; 404 when the listing has no
   * report available (`condition_report.available` on the listing says
   * whether to offer it).
   *
   * @returns {Promise<{ blob: Blob, filename: string|null }>}
   */
  getConditionReport: (houseId) =>
    APIAgent.fetchBlob(`${base(houseId)}/report`, {
      timeout: 30000,
      accept: 'application/pdf',
    }),
};

/** Statuses whose answers can no longer change: signed, or out for signature. */
const LOCKED_STATUSES = new Set(['completed', 'signing']);

const isConflict = (err) => err?.status === 409 || err?.statusCode === 409;
const isUnavailable = (err) => err?.status === 503 || err?.statusCode === 503;

/** Shown when the server can't reach DocuSign (503) and sends no message. */
export const DOCUSIGN_UNAVAILABLE_MESSAGE =
  'DocuSign is not available right now. Your answers are saved. Please try again in a few minutes.';

/** Shown when the sign request outran the client. */
export const SIGN_TIMEOUT_MESSAGE =
  'We could not reach DocuSign in time. Your answers are saved. Please try again.';

/**
 * Send the browser to DocuSign. Kept separate so tests can stand in for a
 * navigation jsdom can't perform.
 */
export const signingNavigator = {
  go: (url) => {
    window.location.assign(url);
  },
};

/**
 * Hook backing the listing wizard's disclosure step.
 *
 * Answers are held locally so typing stays responsive; `save` pushes the
 * accumulated dirty set. Nothing is ever auto-answered — every value must come
 * from the seller (several items invert if inferred; see `derive_forbidden`).
 *
 * Writes are serialized: answer saves, and the preview and signing that
 * depend on them, run one at a time, each sending whatever is pending when it
 * starts. The hook also holds the last server-rendered preview, stored with
 * the caller's `key` — a fingerprint of the caption and answers it was
 * rendered from — so the form can tell whether it still matches what the
 * owner is about to sign.
 *
 * @param {number} houseId
 * @returns form state, answers, the preview, and the save/preview/sign
 *   actions
 */
export const useDisclosureForm = (houseId) => {
  const [form, setFormState] = useState(null);
  const [answers, setAnswersState] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The server's message when the listing's form isn't available yet (503:
  // e.g. the Vacant Land Disclosure Report before its edition is published).
  const [unavailable, setUnavailable] = useState(null);
  // Per-item problems returned by a failed completion attempt (422).
  const [problems, setProblems] = useState([]);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  // { html, sha256, signature_date, key } from the last successful preview.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // True once the browser has been sent to DocuSign.
  const [redirecting, setRedirecting] = useState(false);

  // Queued work reads these when it runs, not when it was scheduled: a save
  // must send what is pending then, under the edition on screen then.
  const formRef = useRef(null);
  const answersRef = useRef({});
  const dirtyRef = useRef({});
  // Tail of the write queue.
  const queueRef = useRef(Promise.resolve());

  const applyForm = useCallback((data) => {
    formRef.current = data;
    setFormState(data);
  }, []);

  const applyAnswers = useCallback((next) => {
    answersRef.current = next;
    setAnswersState(next);
  }, []);

  const applyDirty = useCallback((next) => {
    dirtyRef.current = next;
    setHasUnsaved(Object.keys(next).length > 0);
  }, []);

  const fetchForm = useCallback(async () => {
    if (!houseId) return;
    try {
      setLoading(true);
      setError(null);
      setUnavailable(null);
      setPreview(null);
      const data = await DisclosureAPI.getForm(houseId);
      applyForm(data);
      applyAnswers(data.answers || {});
      applyDirty({});
    } catch (err) {
      if (err?.status === 503 || err?.statusCode === 503) {
        // Not an error the seller can fix: the form isn't offered yet.
        setUnavailable(err.message || 'This disclosure report is not available yet.');
      } else {
        console.error('Error loading disclosure form:', err);
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [houseId, applyForm, applyAnswers, applyDirty]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  /**
   * Re-read the form and answers in the background: no loading screen, and
   * the message and problems on screen stay put.
   *
   * A completed report shows exactly what was signed, and one out for
   * signature shows exactly what is being signed (its answers are locked), so
   * the server's answers replace the local ones and nothing stays pending; the same goes with
   * `discardPending`, when the page's questions were out of date. Otherwise
   * the seller's unsaved edits are laid over the server's answers and stay
   * pending.
   */
  const reloadForm = useCallback(
    async ({ discardPending = false } = {}) => {
      if (!houseId) return;
      try {
        const data = await DisclosureAPI.getForm(houseId);
        const serverAnswers = data.answers || {};
        applyForm(data);
        if (discardPending || LOCKED_STATUSES.has(data.status)) {
          applyAnswers(serverAnswers);
          applyDirty({});
        } else {
          applyAnswers({ ...serverAnswers, ...dirtyRef.current });
        }
      } catch (err) {
        console.error('Error reloading disclosure form:', err);
      }
    },
    [houseId, applyForm, applyAnswers, applyDirty]
  );

  /** Run `task` once every write queued before it has settled. */
  const enqueue = useCallback((task) => {
    const run = queueRef.current.then(() => task());
    // A failure is the caller's to handle; the queue carries on.
    queueRef.current = run.catch(() => {});
    return run;
  }, []);

  /**
   * Send what is pending, for the edition on screen. Only call it inside the
   * queue.
   *
   * Afterwards only the entries unchanged since they were sent are cleared:
   * an answer edited while its save was in flight stays pending for the next
   * save. A 409 means the questions on screen are out of date (or the report
   * was completed elsewhere): the form and answers are reloaded from the
   * server, the pending answers — given to questions that no longer read that
   * way — and the preview are dropped, and the error is rethrown, marked
   * `formReloaded`, for the caller to show.
   */
  const sendDirty = useCallback(async () => {
    const pending = dirtyRef.current;
    if (Object.keys(pending).length === 0) return null;
    let status;
    try {
      status = await DisclosureAPI.saveAnswers(
        houseId,
        pending,
        formRef.current?.edition,
        formRef.current?.form
      );
    } catch (err) {
      if (isConflict(err)) {
        setPreview(null);
        await reloadForm({ discardPending: true });
        err.formReloaded = true;
      }
      throw err;
    }
    const rest = { ...dirtyRef.current };
    Object.keys(pending).forEach((itemId) => {
      if (rest[itemId] === pending[itemId]) delete rest[itemId];
    });
    applyDirty(rest);
    // The server tracks which re-worded questions still need an answer; take
    // the notice's list from it.
    if (formRef.current?.requestioned_items?.length) await reloadForm();
    return status;
  }, [houseId, reloadForm, applyDirty]);

  /** Record an answer locally and mark it for the next save. */
  const setAnswer = useCallback(
    (itemId, value, explanation) => {
      const current = answersRef.current;
      const entry = {
        value,
        explanation:
          explanation !== undefined
            ? explanation
            : (current[itemId]?.explanation ?? null),
      };
      applyAnswers({ ...current, [itemId]: entry });
      applyDirty({ ...dirtyRef.current, [itemId]: entry });
    },
    [applyAnswers, applyDirty]
  );

  const setExplanation = useCallback(
    (itemId, explanation) => {
      const current = answersRef.current;
      const entry = { value: current[itemId]?.value ?? null, explanation };
      applyAnswers({ ...current, [itemId]: entry });
      applyDirty({ ...dirtyRef.current, [itemId]: entry });
    },
    [applyAnswers, applyDirty]
  );

  /** Push pending answers. No-op when nothing is dirty. */
  const save = useCallback(async () => {
    if (!houseId || Object.keys(dirtyRef.current).length === 0) return null;
    try {
      setSaving(true);
      setError(null);
      return await enqueue(sendDirty);
    } catch (err) {
      console.error('Error saving disclosure answers:', err);
      setError(err.message);
      return null;
    } finally {
      setSaving(false);
    }
  }, [houseId, enqueue, sendDirty]);

  /**
   * Have the server render the report for review. Pending answers are saved
   * first, because the preview is rendered from what is saved. Any earlier
   * preview is dropped as soon as a new one is asked for.
   *
   * @param {object} caption - see `DisclosureAPI.getPreview`
   * @param {string} key - the caller's fingerprint of what is being previewed
   * @returns the stored preview, or null when none could be made (see
   *   `error`, and `problems` for a 422)
   */
  const requestPreview = useCallback(
    async (caption, key) => {
      if (!houseId) return null;
      try {
        setPreviewing(true);
        setError(null);
        setProblems([]);
        setPreview(null);
        return await enqueue(async () => {
          await sendDirty();
          const result = await DisclosureAPI.getPreview(houseId, caption);
          const stored = { ...result, key };
          setPreview(stored);
          // The page was open past midnight (Central time): the server signs
          // with a new date now, and a question may have taken effect with
          // it. Reload so the page's date and questions match this preview —
          // otherwise the form would call every new preview out of date.
          const formDate = formRef.current?.signature_date;
          if (
            result?.signature_date &&
            formDate &&
            result.signature_date !== formDate
          ) {
            await reloadForm();
          }
          return stored;
        });
      } catch (err) {
        if (err?.formReloaded) {
          setError(err.message);
          return null;
        }
        const detail = err?.detail || err?.data?.detail;
        if (detail && Array.isArray(detail.problems)) {
          setProblems(detail.problems);
          setError(detail.message || 'The report cannot be previewed yet.');
          return null;
        }
        console.error('Error previewing disclosure:', err);
        setError(err.message);
        // e.g. the report was completed in another tab.
        if (isConflict(err)) await reloadForm();
        return null;
      } finally {
        setPreviewing(false);
      }
    },
    [houseId, enqueue, sendDirty, reloadForm]
  );

  /**
   * Send the owner to DocuSign to sign the report. Flushes pending answers
   * first so the server renders what the seller actually sees, then navigates
   * to the signing URL. Also resumes an envelope that is already out (status
   * `signing`): the server reuses it.
   *
   * @returns the sign response (`{ document_id, envelope_id, signing_url }`)
   *   once the browser is on its way to DocuSign, or null when validation
   *   failed (see `problems`, which anchors each message to its item), the
   *   signing was refused as out of date (see `error`; the preview is
   *   cleared), or DocuSign is unavailable (see `error`).
   */
  const sign = useCallback(
    async (payload) => {
      if (!houseId) return null;
      let leaving = false;
      try {
        setSaving(true);
        setError(null);
        setProblems([]);
        return await enqueue(async () => {
          await sendDirty();
          const result = await DisclosureAPI.sign(houseId, payload);
          if (!result?.signing_url) {
            throw new Error('DocuSign did not return a signing link. Please try again.');
          }
          leaving = true;
          setRedirecting(true);
          signingNavigator.go(result.signing_url);
          return result;
        });
      } catch (err) {
        if (err?.formReloaded) {
          setError(err.message);
          return null;
        }
        const detail = err?.detail || err?.data?.detail;
        if (detail && Array.isArray(detail.problems)) {
          setProblems(detail.problems);
          setError(detail.message || 'The report is not complete yet.');
          // A question the server names may not be on this page: one that
          // took effect after it loaded (E3m, at midnight on 2026-10-01). The
          // answers were saved first, so reload the form and answers.
          await reloadForm();
          return null;
        }
        // The report changed after the preview, the certification wording was
        // updated, or reports aren't being accepted yet. The preview is void
        // either way; the owner has to review a fresh one before signing.
        if (isConflict(err)) {
          setPreview(null);
          setError(err.message);
          await reloadForm();
          return null;
        }
        if (isUnavailable(err)) {
          const message = typeof err?.detail === 'string' ? err.detail : null;
          setError(message || DOCUSIGN_UNAVAILABLE_MESSAGE);
          return null;
        }
        // A client-side timeout says we stopped waiting, not that the server
        // failed. Re-read the form: if the envelope did go out, the report is
        // now `signing` and the page offers "Continue signing in DocuSign",
        // which reuses it.
        if (err?.isTimeout) {
          await reloadForm();
          setError(SIGN_TIMEOUT_MESSAGE);
          return null;
        }
        console.error('Error starting the DocuSign signature:', err);
        setError(err.message);
        return null;
      } finally {
        // Leaving for DocuSign: keep the buttons disabled until the page goes.
        if (!leaving) setSaving(false);
      }
    },
    [houseId, enqueue, sendDirty, reloadForm]
  );

  return {
    form,
    answers,
    loading,
    saving,
    error,
    unavailable,
    problems,
    hasUnsaved,
    preview,
    previewing,
    setAnswer,
    setExplanation,
    save,
    redirecting,
    requestPreview,
    sign,
    refetch: fetchForm,
  };
};

export default DisclosureAPI;
